import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
////////////////
// HELPERS
///////////////
/**
 * useActionProps
 * A custom hook that manages the state of the action props.
 * The state is updated based on the prop value.
 * If the prop value is updated, the state is updated.
 * If the prop value is not updated, the state is not updated.
 * @param initialProps - The initial action props.
 * @returns The action props and the setter function for the action props.
 */
export const useActionProps = (initialProps) => {
    const [actionProps, setActionPropsState] = useState(initialProps);
    const setActionProps = (props) => {
        setActionPropsState((prevProps) => {
            const newProps = {
                ...prevProps,
                ...props,
            };
            const isUpdateNeeded = Object.keys(props).some((key) => props[key] !== prevProps[key]);
            return isUpdateNeeded ? newProps : prevProps;
        });
    };
    return [actionProps, setActionProps];
};
/**
 * useManagedState
 *
 * A custom hook that manages state based on whether the component is controlled or uncontrolled.
 * If the component is controlled, the state is updated based on the prop value.
 * If the component is uncontrolled, the state is updated based on the internal state.
 *
 * @param propValue - The prop value passed to the component.
 * @param defaultValue - The default value for the state.
 * @param externalSetState - The external setter function for the state.
 * @returns The state and the setter function for the state.
 */
const useManagedState = (propValue, defaultValue, externalSetState) => {
    const [state, setState] = useState(propValue !== undefined ? propValue : defaultValue);
    const isControlled = useRef(propValue !== undefined);
    const isInternalUpdate = useRef(false);
    useEffect(() => {
        if (isControlled.current && propValue !== undefined && !isInternalUpdate.current) {
            setState(propValue);
        }
        isInternalUpdate.current = false;
    }, [propValue]);
    useEffect(() => {
        if (isControlled.current && !externalSetState) {
            console.warn('Warning: A controlled component is missing an external setter function.');
        }
    }, [isControlled.current, externalSetState]);
    const setManagedState = useCallback((value) => {
        if (externalSetState) {
            externalSetState(value);
        }
        else if (!isControlled.current) {
            setState(value);
        }
        else {
            isInternalUpdate.current = true;
            setState(value);
        }
    }, [externalSetState]);
    return [state, setManagedState];
};
/////////////////
// CONTEXT
////////////////
const DrawerContext = createContext(undefined);
/**
 * Custom hook to access the drawer context.
 *
 * This hook provides access to the drawer state and actions within the `DrawerContextProvider`.
 * It throws an error if used outside of a `DrawerContextProvider`.
 *
 * @returns {DrawerState} The current drawer state and actions.
 *
 * @throws {Error} If the hook is used outside of a `DrawerContextProvider`.
 */
export const useDrawerContext = () => {
    const context = useContext(DrawerContext);
    if (!context) {
        throw new Error('useDrawerContext must be used within a DrawerContextProvider');
    }
    return context;
};
/////////////////
// PROVIDER
////////////////
export const DrawerContextProvider = (props) => {
    const { children, isDrawerOpen: initialIsDrawerOpen, setDrawerIsOpen: externalSetDrawerIsOpen, isLoading: initialIsLoading, setLoading: externalSetLoading, primaryActionProps: initialPrimaryActionProps, secondaryActionProps: initialSecondaryActionProps, tertiaryActionProps: initialTertiaryActionProps, dismissActionProps: initialDismissActionProps, headerProps: initialHeaderProps, } = props;
    const [isLoading, setLoading] = useManagedState(initialIsLoading, false, externalSetLoading);
    const [isDrawerOpen, setDrawerIsOpen] = useManagedState(initialIsDrawerOpen, false, externalSetDrawerIsOpen);
    const [primaryActionProps, setPrimaryActionButton] = useActionProps(initialPrimaryActionProps || {
        label: undefined,
        disabled: false,
        hidden: false,
    });
    const [secondaryActionProps, setSecondaryActionButton] = useActionProps(initialSecondaryActionProps || {
        label: 'Back',
        disabled: false,
        hidden: undefined,
    });
    const [tertiaryActionProps, setTertiaryActionButton] = useActionProps(initialTertiaryActionProps || {
        label: 'Clear',
        disabled: false,
        hidden: true,
    });
    const [dismissActionProps, setDismissActionButton] = useActionProps(initialDismissActionProps || {
        label: 'Cancel',
        disabled: false,
        hidden: false,
    });
    const [headerProps, setHeaderProps] = useManagedState(initialHeaderProps, { title: '', caption: '' });
    const contextValue = useMemo(() => ({
        isLoading,
        isDrawerOpen,
        setDrawerIsOpen,
        setLoading,
        primaryActionProps,
        setPrimaryActionButton,
        secondaryActionProps,
        setSecondaryActionButton,
        tertiaryActionProps,
        setTertiaryActionButton,
        dismissActionProps,
        setDismissActionButton,
        headerProps,
        setHeaderProps,
    }), [
        isLoading,
        isDrawerOpen,
        primaryActionProps,
        secondaryActionProps,
        setLoading,
        tertiaryActionProps,
        dismissActionProps,
        headerProps,
        setDrawerIsOpen,
        setPrimaryActionButton,
        setSecondaryActionButton,
        setTertiaryActionButton,
        setDismissActionButton,
        setHeaderProps,
    ]);
    return _jsx(DrawerContext.Provider, { value: contextValue, children: children });
};
